import { useEffect, useRef, type ReactNode } from 'react';
import type { LogEntry } from '../lib/api.js';
import { EmptyState } from './primitives.js';

/**
 * Streaming log view.
 *
 * It follows the tail only while the operator is already at the bottom; the
 * moment they scroll up to read something, it stops yanking the view away.
 */
export function LogViewer({ logs }: { logs: LogEntry[] }): ReactNode {
  const container = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    const element = container.current;
    if (!element || !stickToBottom.current) return;
    element.scrollTop = element.scrollHeight;
  }, [logs]);

  if (logs.length === 0) {
    return <EmptyState title="No output yet" body="Log lines from the runner appear here as it works." />;
  }

  const onScroll = (): void => {
    const element = container.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickToBottom.current = distanceFromBottom < 40;
  };

  return (
    <div className="logs" ref={container} onScroll={onScroll} role="log" aria-live="polite">
      {logs.map((entry) => (
        <div key={entry.id} className={`logs__line logs__line--${entry.level}`}>
          <span className="logs__time">{new Date(entry.at).toLocaleTimeString()}</span>
          <span>{entry.message}</span>
        </div>
      ))}
    </div>
  );
}
